import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatMoney } from "@/lib/currencies";

/**
 * useAppSettings — reads and mutates `/api/me/settings`.
 *
 * PD-014 / DM-001 Domain 3: App Settings.
 * BR-CONTEXT-001: resolution order is user override → Home Country default →
 * Generic default. The backend returns BOTH `stored` (what the user has
 * explicitly persisted) and `effective` (the fully resolved values). The
 * hook exposes both so the UI can show "Auto (Home Country)" vs Overridden.
 *
 * Pass `{ enabled: false }` to skip the fetch when the caller isn't yet
 * authenticated (e.g. on the login screen). All formatter helpers below
 * gracefully fall back when `effective` is empty.
 */
export function useAppSettings(options = {}) {
  const enabled = options.enabled !== false;
  const [state, setState] = useState({
    loading: enabled,
    error: null,
    stored: {},
    effective: {},
    home_country_catalogue: [],
  });

  const load = useCallback(async () => {
    if (!enabled) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const r = await api.get("/api/me/settings");
      setState({
        loading: false,
        error: null,
        stored: r.data.stored || {},
        effective: r.data.effective || {},
        home_country_catalogue: r.data.home_country_catalogue || [],
      });
    } catch (e) {
      // Not fatal — sidebar and other consumers just fall back to generic
      // defaults via the formatter helpers below.
      setState((s) => ({ ...s, loading: false, error: formatApiError(e) }));
    }
  }, [enabled]);

  useEffect(() => { load(); }, [load]);

  const patch = useCallback(async (partial) => {
    const r = await api.patch("/api/me/settings", partial);
    setState((s) => ({
      ...s,
      stored: r.data.stored || {},
      effective: r.data.effective || {},
    }));
    return r.data;
  }, []);

  return { ...state, reload: load, patch };
}


/**
 * Presentation-layer Regional Intelligence formatters.
 *
 * ⚠️  These NEVER mutate source data. They read the resolved `effective`
 * settings block and return a formatted string. Callers keep the raw value
 * alongside the presentation so we can toggle locales without losing data.
 */

const CURRENCY_SYMBOLS = {
  USD: "$", CAD: "C$", GBP: "£", AUD: "A$", EUR: "€", JPY: "¥",
};

const currencySymbol = (code) =>
  code && CURRENCY_SYMBOLS[code.toUpperCase()] ? CURRENCY_SYMBOLS[code.toUpperCase()] : code;

/**
 * formatCurrency — BR-FINANCE-009 · thin adapter over the canonical
 * `formatMoney` helper in `@/lib/currencies`. Reads the resolved
 * `effective.currency` from Regional Intelligence and delegates the
 * actual string shaping so every Finance surface renders through
 * the same formatter (no magnitude-based rounding, always two
 * decimals for standard-minor-unit currencies, JPY 0-decimal).
 */
export function formatCurrency(amount, effective) {
  return formatMoney(amount, effective?.currency || "USD");
}

export function formatTemperature(fahrenheit, effective) {
  if (fahrenheit == null || Number.isNaN(Number(fahrenheit))) return "—";
  const unit = effective?.temperature_unit || "C";
  const f = Number(fahrenheit);
  if (unit === "F") return `${Math.round(f)}°F`;
  const c = ((f - 32) * 5) / 9;
  return `${Math.round(c)}°C`;
}

export function formatDistance(miles, effective) {
  if (miles == null || Number.isNaN(Number(miles))) return "—";
  const unit = effective?.distance_unit || "km";
  const m = Number(miles);
  if (unit === "mi") return `${m.toLocaleString(undefined, { maximumFractionDigits: 1 })} mi`;
  const km = m * 1.60934;
  return `${km.toLocaleString(undefined, { maximumFractionDigits: 1 })} km`;
}

export function formatDate(isoDate, effective) {
  if (!isoDate) return "—";
  const d = new Date(isoDate.length === 10 ? `${isoDate}T00:00:00Z` : isoDate);
  if (Number.isNaN(d.valueOf())) return isoDate;
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const fmt = effective?.date_format || "YYYY-MM-DD";
  return fmt
    .replace("YYYY", yyyy)
    .replace("MM", mm)
    .replace("DD", dd);
}

export function formatTime(isoDateTime, effective) {
  if (!isoDateTime) return "—";
  const d = new Date(isoDateTime);
  if (Number.isNaN(d.valueOf())) return isoDateTime;
  const hh24 = d.getHours();
  const mm = String(d.getMinutes()).padStart(2, "0");
  const fmt = effective?.time_format || "24h";
  if (fmt === "24h") return `${String(hh24).padStart(2, "0")}:${mm}`;
  const period = hh24 >= 12 ? "PM" : "AM";
  const hh12 = ((hh24 + 11) % 12) + 1;
  return `${hh12}:${mm} ${period}`;
}
